import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

/** Brand easing. Long, decelerating, never bouncy — restraint is the brand. */
export const EASE = [0.16, 1, 0.3, 1];
export const EASE_OUT = [0.22, 1, 0.36, 1];
export const EASE_IN_OUT = [0.65, 0, 0.35, 1];

export const DUR = { fast: 0.22, base: 0.42, slow: 0.8, reveal: 0.9 };

export const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: DUR.slow, ease: EASE } },
};

export const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: DUR.reveal, ease: EASE } },
};

export const scaleIn = {
  hidden: { opacity: 0, scale: 0.985 },
  show: { opacity: 1, scale: 1, transition: { duration: DUR.slow, ease: EASE } },
};

export const rise = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.62, ease: EASE } },
};

export const staggerParent = (stagger = 0.09, delayChildren = 0) => ({
  hidden: {},
  show: { transition: { staggerChildren: stagger, delayChildren } },
});

export const VIEWPORT = { once: true, amount: 0, margin: "0px 0px 0px 0px" };
export const VIEWPORT_EAGER = { once: true, amount: 0, margin: "0px 0px 0px 0px" };

/** Drawer / menu / panel presence variants. */
export const menuDrop = {
  hidden: { opacity: 0, y: -8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.26, ease: EASE_OUT } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.16, ease: "easeIn" } },
};

export const drawerLeft = {
  hidden: { x: "-100%" },
  show: { x: 0, transition: { duration: 0.36, ease: EASE_OUT } },
  exit: { x: "-100%", transition: { duration: 0.24, ease: "easeIn" } },
};

export const drawerBottom = {
  hidden: { y: "100%" },
  show: { y: 0, transition: { duration: 0.36, ease: EASE_OUT } },
  exit: { y: "100%", transition: { duration: 0.24, ease: "easeIn" } },
};

export const scrim = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.24 } },
  exit: { opacity: 0, transition: { duration: 0.18 } },
};

export const panelSwap = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.32, ease: EASE_OUT } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.16, ease: "easeIn" } },
};

export const stepSwap = {
  hidden: { opacity: 0, x: 24 },
  show: { opacity: 1, x: 0, transition: { duration: 0.4, ease: EASE_OUT } },
  exit: { opacity: 0, x: -18, transition: { duration: 0.2, ease: "easeIn" } },
};

/**
 * Scroll-triggered reveal. Replaces the old on-mount CSS animation so that
 * sections below the fold actually animate when the reader reaches them.
 */
export function Reveal({
  children, className = "", delay = 0, y = 22, as = "div",
  variants, viewport = VIEWPORT, ...rest
}) {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;
  const v = variants || {
    hidden: { opacity: 0, y: reduce ? 0 : y },
    show: { opacity: 1, y: 0, transition: { duration: DUR.slow, ease: EASE, delay } },
  };
  return (
    <Tag
      className={className}
      variants={v}
      initial="hidden"
      whileInView="show"
      viewport={viewport}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/** Parent that staggers its StaggerItem children into view. */
export function Stagger({
  children, className = "", stagger = 0.09, delayChildren = 0,
  as = "div", viewport = VIEWPORT, ...rest
}) {
  const Tag = motion[as] || motion.div;
  return (
    <Tag
      className={className}
      variants={staggerParent(stagger, delayChildren)}
      initial="hidden"
      whileInView="show"
      viewport={viewport}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function StaggerItem({ children, className = "", as = "div", variants = rise, ...rest }) {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;
  const v = reduce ? fadeIn : variants;
  return (
    <Tag className={className} variants={v} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * Mask reveal for Fraunces headings — each line rises out from behind a clip.
 * Pass an array of lines; ReactNode per line is fine.
 */
export function LineReveal({
  lines, className = "", delay = 0, lineClassName = "",
  stagger = 0.11, duration = 0.95, inViewAmount = null,
}) {
  const reduce = useReducedMotion();
  if (reduce) {
    return (
      <span className={className}>
        {lines.map((l, i) => (
          <React.Fragment key={i}>
            <span className={`block ${lineClassName}`}>{l}</span>
            {i < lines.length - 1 && " "}
          </React.Fragment>
        ))}
      </span>
    );
  }
  const trigger = inViewAmount
    ? { whileInView: "show", viewport: { once: true, amount: inViewAmount } }
    : { animate: "show" };
  return (
    <motion.span
      className={className}
      initial="hidden"
      {...trigger}
      variants={staggerParent(stagger, delay)}
    >
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          <span className="block overflow-hidden pb-[0.06em]">
            <motion.span
              className={`block ${lineClassName}`}
              variants={{
                hidden: { y: "110%" },
                show: { y: "0%", transition: { duration, ease: EASE } },
              }}
            >
              {line}
            </motion.span>
          </span>
          {/* Whitespace between lines so the concatenated DOM text reads
              naturally for screen readers, copy-paste and search engines
              (fixes "peaceand" defect D4). */}
          {i < lines.length - 1 && " "}
        </React.Fragment>
      ))}
    </motion.span>
  );
}

/** Tabular-nums count-up, triggered on scroll into view. */
export function CountUp({
  value, decimals = 0, suffix = "", prefix = "", className = "", duration = 1100, testId,
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const reduce = useReducedMotion();
  const [n, setN] = useState(0);

  useEffect(() => {
    if (reduce || !inView) {
      if (reduce) setN(value);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / duration, 1);
      setN(value * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration, reduce]);

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {prefix}
      {n.toFixed(decimals)}
      {suffix}
    </span>
  );
}

/** Restrained hover lift used on cards. No shadow — border and translate only. */
export const hoverLift = {
  rest: { y: 0 },
  hover: { y: -3, transition: { duration: 0.28, ease: EASE_OUT } },
};

export { motion, AnimatePresence } from "framer-motion";
export { useReducedMotion } from "framer-motion";
